import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Check, LayoutDashboard } from '../../../icons';
import { useAuthStore } from '../../../store/authStore';
import { listMyTemplates } from '../../../api/dashboard-templates';
import { DashboardTabs } from './DashboardTabs';
import { BoardSettingsModal, BoardDeleteModal } from './BoardSettingsModal';
import {
  useDashboardStore, BOARD_KIND_LABEL, BOARD_VISIBILITY_LABEL,
  type BoardKind, type DashboardBoard,
} from '../../../store/dashboardStore';
import { useProfileUiStore } from '../../../store/profileUiStore';
import { WidgetGrid } from './WidgetGrid';
// 날씨 고정 줄 (2026-09-21 · 사용자 결정 ⓑ) — 위젯이 아니라 «화면의 머리»다. 날씨 위젯을 담은 보드에서는 빠진다.
import { WeatherStrip } from '../../../components/Weather/WeatherStrip';
import { getWidgetDef } from './widget-registry';
import { WidgetActionsContext, type AttendanceTab, type WidgetActions } from './widget-context';
import { AttendanceRequestModal } from '../modals/AttendanceRequestModal';
import { DashboardCreateModal } from '../modals/DashboardCreateModal';
import { PersonalSettingsModal } from '../modals/PersonalSettingsModal';
import { WidgetPickerModal } from '../modals/WidgetPickerModal';
import { AiAuraFab } from '../../../components/AiAuraFab';
import { useAiPanelStore } from '../../../store/aiPanelStore';
import { useAiChatStore } from '../../../store/aiChatStore';
import './board.css';
// 대시보드 시안 v2 V5 (2026-09-16) — 대시보드 틀 새 모양. 깃발이 꺼져 있으면(기본) 종전 툴바 · 빈 칸 · 설정 창 그대로.
import { DASHBOARD_V2_ENABLED } from '../../../lib/layout-shell';
import { BoardHeadV2, EditBandV2, FirstVisitV2, LockedBandV2, myBoardTarget } from './DashboardFrameV2';
import { BoardSettingsV2Modal } from './BoardSettingsV2Modal';
import type { CreateBoardPreset } from './starter-templates';
// 대시보드 시안 v2 V6 (2026-09-16) — 새 대시보드 창 v2. 깃발이 꺼져 있으면 종전 창 그대로.
import { DashboardCreateV2Modal } from '../modals/DashboardCreateV2Modal';

/**
 * 대시보드 위젯 보드 — all-frame.html #2a(나의) · #2b(전사).
 *
 * 구성: 탭 + 툴바 + 12-컬럼 위젯 그리드.
 * (2026-08-12) ② 좌측 상세(대시보드 목록) 제거 — 보드 전환은 상단 탭이 전담한다.
 * 전사 보드는 `locked` — 툴바·드래그·⋯ 메뉴가 모두 사라지고 잠금 표시만 남는다(같은 컴포넌트).
 *
 * 모달 3종(#2c 근태 신청 · #2d 새 대시보드 · #2e 개인 설정)의 열림 상태를 여기서 소유하고,
 * 위젯에는 context 로 여는 함수만 내려보낸다(위젯은 모달의 존재를 모른다).
 */

type ModalState =
  | { kind: 'none' }
  | { kind: 'attendance'; tab: AttendanceTab }
  | { kind: 'create-board'; preset?: CreateBoardPreset }
  | { kind: 'board-settings'; board: DashboardBoard }
  | { kind: 'board-delete'; board: DashboardBoard }
  | { kind: 'add-widget' }
  | { kind: 'settings' };

export function DashboardBoard() {
  const navigate = useNavigate();
  // AI 단추가 여는 것은 «우측 패널»이다(설계 판 A) — 화면을 떠나지 않는다.
  const openAiPanel = useAiPanelStore((st) => st.openPanel);
  /* ★★ 이 단추는 **무조건 새 대화**다(2026-09-11 사용자 지시) — 아래 `startAi` 참조. */
  const newAiConversation = useAiChatStore((st) => st.newConversation);
  /**
   * ★★★ **대시보드의 AI 단추는 «무조건 새 대화»다** (2026-09-11 사용자 지시).
   *
   * 여태는 패널만 열어서, 어제 하던 이야기가 그대로 이어졌다. 대시보드에서 누르는 것은
   * 「지금 막 떠오른 것을 묻는」 자리라, **앞의 맥락이 붙으면 답이 엉뚱해진다.**
   * ⭑ 지난 대화는 **목록에 그대로 남는다**(화면만 비운다) — 잃는 것이 없다.
   * ⭑ 고른 에이전트는 남는다 — 「새 대화」는 대화를 비우는 것이지 에이전트를 놓는 것이 아니다.
   */
  const startAi = useCallback((): void => {
    newAiConversation();
    openAiPanel();
  }, [newAiConversation, openAiPanel]);

  const addWidgets = useDashboardStore((s) => s.addWidgets);
  const userId = useAuthStore((s) => s.user?.id);
  // 잠김 띠의 「통합설정 › 위젯 관리」 링크 — 그 판(/settings)을 여는 사람만(SettingsPage 의 판정과 같다).
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'ADMIN' || role === 'SYSTEM_ADMIN';
  const boards = useDashboardStore((s) => s.boards);
  const activeBoardId = useDashboardStore((s) => s.activeBoardId);
  const editing = useDashboardStore((s) => s.editing);
  const hydrate = useDashboardStore((s) => s.hydrate);
  const toggleEditing = useDashboardStore((s) => s.toggleEditing);
  const applyTemplates = useDashboardStore((s) => s.applyTemplates);
  const hiddenBoardIds = useDashboardStore((s) => s.hiddenBoardIds);
  const defaultBoardId = useDashboardStore((s) => s.defaultBoardId);
  const setActiveBoard = useDashboardStore((s) => s.setActiveBoard);

  const [modal, setModal] = useState<ModalState>({ kind: 'none' });
  const closeModal = useCallback(() => setModal({ kind: 'none' }), []);

  useEffect(() => {
    hydrate(userId != null ? String(userId) : '');
  }, [hydrate, userId]);

  /**
   * 회사 기본판 (2026-09-08 · migration 173 · 시안 화면 I) — hydrate 로 브라우저 배치를 세운
   * **뒤에** 서버가 정한 것을 얹는다.
   *
   * ★ 실패하면 조용히 넘어간다 — 서버가 못 답한다고 제 배치를 못 보는 것이 더 나쁘다.
   *   그때는 브라우저에 있던 배치가 그대로 산다(지금까지의 동작).
   */
  useEffect(() => {
    let alive = true;
    void listMyTemplates()
      /* ★★★ **비어 있어도 반영한다** (2026-09-09). 종전에는 `rows.length > 0` 일 때만 불러서,
             회사가 **아무것도 안 정한 상태**가 화면에 닿지 않았다 — 그래서 앱에 박힌
             「전사 대시보드」 탭이 회사가 정한 적 없는 위젯 다섯을 달고 떠 있었다.
             실패(catch)는 그대로 아무것도 안 한다 — 못 읽은 것과 «없다»는 다르다. */
      .then((rows) => { if (alive) applyTemplates(rows); })
      .catch(() => { /* 기본판을 못 읽어도 화면은 선다 */ });
    return () => { alive = false; };
  }, [applyTemplates, userId]);

  const board = boards.find((b) => b.id === activeBoardId) ?? boards[0];

  // context 값은 memo 로 고정 — 매 렌더 새 객체를 넘기면 위젯 12개가 통째로 리렌더된다.
  const actions = useMemo<WidgetActions>(() => ({
    go: (to) => navigate(to),
    openAttendanceRequest: (tab = 'leave') => setModal({ kind: 'attendance', tab }),
    // 2026-08-12 — 설정 진입점을 전역 설정 모달(AppLayout 소유)로 옮긴다. 종전의 보드 전용
    // #2e 모달(PersonalSettingsModal)은 파일을 남겨두되 더 이상 열지 않는다: 설정 창이 두 벌로
    // 갈라지면 같은 항목이 화면마다 다르게 보인다. 롤백은 이 한 줄을 되돌리면 된다.
    openSettings: () => useProfileUiStore.getState().openSettings(),
  }), [navigate]);

  /**
   * ★★★ **보드가 하나도 없을 수 있다** (2026-09-09) — 앱이 미리 지어 두던 세 판을 없앴다.
   *     종전에는 `return null` 이라 **흰 화면**이었다. 처음 여는 사람이 보는 것이 흰 화면이면
   *     그것은 고장이다 — 탭 줄(「+」 가 있는)과 **무엇을 하면 되는지**를 함께 세운다.
   */
  if (!board) {
    return (
      <WidgetActionsContext.Provider value={actions}>
        <div className={`dw-shell${DASHBOARD_V2_ENABLED ? ' dwv-frame' : ''}`}>
          <div className="dw-main">
            <DashboardTabs
              boards={boards}
              activeBoardId=""
              onCreate={() => setModal({ kind: 'create-board' })}
              onSettings={(b) => setModal({ kind: 'board-settings', board: b })}
              onDelete={(b) => setModal({ kind: 'board-delete', board: b })}
            />
            <div className="dw-board scrollbar-thin">
              <WeatherStrip />
              {DASHBOARD_V2_ENABLED
                ? (
                  <FirstVisitV2
                    onCreate={() => setModal({ kind: 'create-board' })}
                    onPickTemplate={(preset) => setModal({ kind: 'create-board', preset })}
                  />
                )
                : (
                  <button type="button" className="dw-dropzone dw-dropzone--empty" onClick={() => setModal({ kind: 'create-board' })}>
                    <LayoutDashboard size={18} aria-hidden />
                    대시보드가 없습니다 · 눌러서 만드세요
                  </button>
                )}
            </div>
          </div>
        </div>
        {modal.kind === 'create-board' && (DASHBOARD_V2_ENABLED
        ? <DashboardCreateV2Modal onClose={closeModal} preset={modal.preset} />
        : <DashboardCreateModal onClose={closeModal} />)}
      </WidgetActionsContext.Provider>
    );
  }

  const locked = Boolean(board.locked);
  const mine = DASHBOARD_V2_ENABLED && locked ? myBoardTarget(boards, hiddenBoardIds, defaultBoardId) : null;

  return (
    <WidgetActionsContext.Provider value={actions}>
      <div className={`dw-shell${DASHBOARD_V2_ENABLED ? ' dwv-frame' : ''}`}>
        <div className="dw-main">
          <DashboardTabs
            boards={boards}
            activeBoardId={board.id}
            onCreate={() => setModal({ kind: 'create-board' })}
            onSettings={(b) => setModal({ kind: 'board-settings', board: b })}
            onDelete={(b) => setModal({ kind: 'board-delete', board: b })}
          />

          {!locked && !DASHBOARD_V2_ENABLED && (
            <div className="dw-toolbar">
              <span className="dw-toolbar__title">{board.name}</span>
              <span className="dw-toolbar__hint">
                {/* ★ 시안 화면 H 의 곁말은 「부서 대시보드 · 위젯 5개 · **부서 공유**」다 —
                       어디까지 보이는지가 빠져 있었다(2026-09-09). */}
                {editing
                  ? '위젯 핸들을 끌어 위치를 바꾸세요'
                  : `${BOARD_KIND_LABEL[board.kind]} 대시보드 · 위젯 ${board.widgets.length}개 · ${BOARD_VISIBILITY_LABEL[board.visibility]}`}
              </span>
              <div className="dw-toolbar__actions">
                <button type="button" className={`dw-btn${editing ? ' dw-btn--primary' : ''}`} onClick={toggleEditing}>
                  {editing ? <Check size={14} aria-hidden /> : <Edit size={14} aria-hidden />}
                  {editing ? '편집 완료' : '레이아웃 편집'}
                </button>
                <button type="button" className="dw-btn dw-btn--primary" onClick={() => setModal({ kind: 'add-widget' })}>
                  <Plus size={14} aria-hidden />위젯 추가
                </button>
              </div>
            </div>
          )}

          <div className={`dw-board scrollbar-thin${editing ? ' is-editing' : ''}`}>
            {DASHBOARD_V2_ENABLED && !locked && (
              <BoardHeadV2
                board={board}
                editing={editing}
                onToggleEditing={toggleEditing}
                onAddWidget={() => setModal({ kind: 'add-widget' })}
                onSettings={() => setModal({ kind: 'board-settings', board })}
              />
            )}
            {DASHBOARD_V2_ENABLED && !locked && editing && <EditBandV2 />}
            {DASHBOARD_V2_ENABLED && locked && (
              <LockedBandV2
                onGoMine={mine ? () => setActiveBoard(mine.id) : undefined}
                onOpenWidgetAdmin={isAdmin ? () => navigate('/settings?pane=widgetlayout') : undefined}
              />
            )}
            {/* ★ 같은 값이 한 화면에 둘이면 「하나가 틀렸다」로 읽힌다 — 위젯을 담았으면 줄을 뺀다(보드마다). */}
            {!board.widgets.some((w) => w.widgetKey === 'weather') && <WeatherStrip />}
            {board.widgets.length === 0
              ? (
                <button type="button" className="dw-dropzone dw-dropzone--empty" onClick={() => setModal({ kind: 'add-widget' })}>
                  <LayoutDashboard size={18} aria-hidden />
                  위젯이 없습니다 · 눌러서 추가하세요
                </button>
              )
              : <WidgetGrid board={board} />}
            {!locked && board.widgets.length > 0 && (
              <button type="button" className="dw-dropzone" onClick={() => setModal({ kind: 'add-widget' })}>
                <Plus size={15} aria-hidden />
                {DASHBOARD_V2_ENABLED ? '위젯 추가 · 원하는 위젯을 골라 담고 드래그로 배치' : '위젯 추가'}
              </button>
            )}
          </div>

          {/* AI 단추 — 콘텐츠 영역 우측 하단에 뜬다. 자리 기준은 board.css 의 `.dw-main`.
              ★★★ 2026-09-09 (설계 판 A · 정의서 E1) — **여는 곳을 바꿨다.** 여태 `/chat` 으로
                 «이동»했는데, 그건 목적지가 틀린 것이었다: 이 단추는 곁에서 묻는 자리를 여는
                 것이지 화면을 떠나는 것이 아니다. 이제 우측 AI 패널을 연다(본체는 AppLayout). */}
          <AiAuraFab onClick={startAi} title="AI 와 새 대화" />
        </div>
      </div>

      {modal.kind === 'attendance' && <AttendanceRequestModal initialTab={modal.tab} onClose={closeModal} />}
      {modal.kind === 'create-board' && (DASHBOARD_V2_ENABLED
        ? <DashboardCreateV2Modal onClose={closeModal} preset={modal.preset} />
        : <DashboardCreateModal onClose={closeModal} />)}
      {modal.kind === 'add-widget' && (
        <WidgetPickerModal
          alreadyOn={board.widgets.map((w) => w.widgetKey)}
          /* D3 (2026-09-16) — 창이 보여 준 「기본 폭」 그대로 놓는다(종전에는 전부 1/3). */
          onConfirm={(keys) => addWidgets(board.id, keys, Object.fromEntries(keys.map((k) => [k, getWidgetDef(k)?.defaultSpan])))}
          onClose={closeModal}
        />
      )}
      {modal.kind === 'settings' && <PersonalSettingsModal onClose={closeModal} />}
      {modal.kind === 'board-settings' && DASHBOARD_V2_ENABLED && (
        <BoardSettingsV2Modal
          board={modal.board}
          onClose={closeModal}
          onDelete={(b) => setModal({ kind: 'board-delete', board: b })}
        />
      )}
      {modal.kind === 'board-settings' && !DASHBOARD_V2_ENABLED && (
        <BoardSettingsModal
          board={modal.board}
          onClose={closeModal}
          /* ★ 시안 화면 H — 설정 창 왼쪽 아래에 「대시보드 삭제」가 있다. 확인 창으로 넘긴다. */
          onDelete={(b) => setModal({ kind: 'board-delete', board: b })}
        />
      )}
      {modal.kind === 'board-delete' && <BoardDeleteModal board={modal.board} onClose={closeModal} />}
    </WidgetActionsContext.Provider>
  );
}

/** 보드 유형 목록 — #2d 모달이 쓰는 순서(전사는 담당자 전용이라 마지막·비활성). */
export const CREATABLE_KINDS: BoardKind[] = ['personal', 'team', 'project'];
